import type { SeoLandingConfig } from "../types";

// Fatos com fonte em .claude/skills/seo/reference/fidexa-empresa.md
// (siglas: PEGN, ERP = Portal ERP, NV = Novo Varejo, BAG = Baguete).
const SOURCE_ERP_NV = "Portal ERP e Novo Varejo, ago/2026";
const SOURCE_PEGN = "Pequenas Empresas & Grandes Negócios, ago/2026";

export const crediarioLojaDeCalcados: SeoLandingConfig = {
  path: "/crediario-loja-de-calcados",
  kind: "segmento",
  keyword: "crediário para loja de calçados",
  title: "Crediário para loja de calçados: cobre no WhatsApp",
  description:
    "Cobrança do crediário da sua loja de calçados pelo WhatsApp: lembrete antes do vencimento, acordo de parcelamento e Pix com baixa automática na conversa.",
  breadcrumbLabel: "Crediário para loja de calçados",
  updatedAt: "2026-10-17",
  service: {
    name: "Cobrança de crediário para lojas de calçados",
    serviceType: "Cobrança automatizada de crediário próprio via WhatsApp",
    audience: "Lojas e redes de calçados com crediário próprio",
  },
  sections: [
    {
      type: "hero",
      eyebrow: "Para lojas e redes de calçados",
      title: "Crediário para loja de calçados com a cobrança no automático",
      lead: "A Fidexa lembra o cliente antes do vencimento, cobra no dia e depois do atraso e recebe a parcela por Pix no WhatsApp, com baixa automática.",
      ctaLabel: "Agendar demonstração gratuita",
      secondaryLabel: "Ver como funciona",
      secondaryHref: "#como-funciona",
      reassurance: "Demonstração gratuita com um especialista.",
    },
    {
      type: "proof",
      title: "249 empresas já cobram com a Fidexa, entre elas:",
      clientNames: [
        "Sapataria Avenida",
        "Omara Calçados",
        "Lojas Elder",
        "Tangará",
        "Dantom Multimarcas",
        "Grupo Minas",
      ],
    },
    {
      type: "shortAnswer",
      id: "o-que-e",
      title: "Como funciona a cobrança do crediário numa loja de calçados",
      answer:
        "Com a Fidexa, a loja de calçados conecta a carteira do crediário próprio e os avisos de cobrança passam a sair sozinhos no WhatsApp: o cliente recebe o lembrete antes do vencimento, a mensagem no dia e, se atrasar, novas mensagens da régua. Ele mesmo escolhe a parcela e paga por Pix na conversa, com baixa automática. Quando precisa de acordo, a equipe da loja negocia no mesmo chat.",
      bullets: [
        "Funciona com o TOTVS Linx ou com a planilha que o seu sistema exporta.",
        "Cobra dívidas de qualquer valor, até as de R$ 50 que não compensa levar à Justiça.",
        "A rede inteira de lojas num painel só.",
      ],
    },
    {
      type: "pain",
      id: "inadimplencia-calcados",
      title: "Calçados têm a maior inadimplência do varejo",
      lead: "O crediário próprio vende mais, mas a loja acaba financiando o cliente sem ter gente para acompanhar os vencimentos, mandar as mensagens e conferir os pagamentos.",
      facts: [
        {
          value: "9,53%",
          label: "Inadimplência em calçados, o maior índice do varejo nacional",
          source: `CNDL/SPC Brasil, citado por ${SOURCE_ERP_NV}`,
        },
        {
          value: "20% a 24%",
          label: "Fatia da receita das lojas que vem do crediário",
          source: `GMattos, citado por ${SOURCE_ERP_NV}`,
        },
        {
          value: "15% a 30%",
          label: "Inadimplência ao ano quando uma loja começa na Fidexa",
          source: "Fidexa, segundo o Baguete, set/2026",
        },
      ],
    },
    {
      type: "steps",
      id: "como-funciona",
      title: "Como a Fidexa cobra o crediário da sua loja",
      steps: [
        {
          title: "Conecta a carteira",
          text: "O crediário entra na Fidexa pela integração com o TOTVS Linx ou por planilha com clientes, valores e vencimentos.",
        },
        {
          title: "Régua no WhatsApp",
          text: "Lembrete antes do vencimento, mensagem no dia e cobrança depois do atraso, pela API oficial do WhatsApp.",
        },
        {
          title: "A equipe negocia só o necessário",
          text: "Quando o cliente quer renegociar, a conversa chega ao chat da Fidexa e alguém da loja fecha o acordo de parcelamento ali mesmo.",
        },
        {
          title: "Pix com baixa automática",
          text: "Pelo Fidexa Pay, o cliente escolhe a parcela e paga por Pix sozinho, a qualquer hora. A Fidexa dá a baixa no pagamento.",
        },
      ],
    },
    {
      type: "comparison",
      id: "comparativo",
      title: "Caderno, planilha ou Fidexa: o que muda na cobrança",
      lead: "O mesmo crediário, cobrado de três jeitos.",
      caption:
        "Comparação entre cobrar o crediário da loja de calçados com caderno e ligação, com planilha e WhatsApp do celular, e com a Fidexa",
      columns: [
        { key: "caderno", label: "Caderno e ligação" },
        { key: "planilha", label: "Planilha e WhatsApp do celular" },
        { key: "fidexa", label: "Fidexa", highlight: true },
      ],
      rows: [
        {
          label: "Lembrete antes do vencimento",
          cells: {
            caderno: false,
            planilha: "Manual, quando alguém lembra",
            fidexa: "Automático",
          },
        },
        {
          label: "Cliente paga fora do horário da loja",
          cells: {
            caderno: false,
            planilha: false,
            fidexa: "Sim, pelo Fidexa Pay",
          },
        },
        {
          label: "Número usado na cobrança",
          cells: {
            caderno: "Telefone da loja",
            planilha: "WhatsApp comum da loja",
            fidexa: "API oficial do WhatsApp",
          },
        },
        {
          label: "Várias lojas num lugar só",
          cells: {
            caderno: false,
            planilha: false,
            fidexa: "Painel único da rede",
          },
        },
        {
          label: "Cliente paga por Pix na conversa",
          cells: { caderno: false, planilha: false, fidexa: true },
        },
        {
          label: "Baixa da parcela",
          cells: {
            caderno: "Manual",
            planilha: "Manual",
            fidexa: "Automática",
          },
        },
        {
          label: "Quem faz o trabalho",
          cells: {
            caderno: "Um funcionário",
            planilha: "Um funcionário",
            fidexa: "A régua e o Fidexa Pay; a equipe só negocia",
          },
        },
      ],
    },
    {
      type: "case",
      id: "caso",
      title: "Uma rede de calçados com seis lojas, um ano depois",
      text: "O primeiro cliente da Fidexa foi uma rede de calçados que opera seis lojas. Em um ano com a cobrança no WhatsApp:",
      metrics: [
        {
          value: "30%",
          label: "Menos inadimplência em um ano",
          source: SOURCE_PEGN,
        },
        {
          value: "R$ 1 mi",
          label: "Recebido, aproximadamente",
          source: SOURCE_PEGN,
        },
      ],
    },
    {
      type: "metrics",
      id: "numeros",
      title: "A Fidexa em números",
      metrics: [
        {
          value: "R$ 39,34 mi",
          label: "Recuperados para lojistas",
          source: SOURCE_ERP_NV,
        },
        {
          value: "32,10%",
          label: "Recuperação média da carteira",
          source: "Plataforma Fidexa",
        },
        {
          value: "15 mil",
          label: "Mensagens de cobrança por dia",
          source: SOURCE_ERP_NV,
        },
      ],
    },
    {
      type: "calculator",
      id: "calculadora",
      title: "Quanto a sua loja perde com a inadimplência?",
      text: "Veja quanto do crediário atrasa, quanto volta ao caixa e quanto a loja perde por ano.",
      href: "/ferramentas/calculadora-de-inadimplencia",
      ctaLabel: "Calcular a inadimplência",
    },
    {
      type: "faq",
      id: "perguntas",
      title: "Perguntas frequentes sobre crediário em loja de calçados",
      items: [
        {
          question: "Vale a pena ter crediário próprio numa loja de calçados?",
          answer:
            "O crediário próprio responde por 20% a 24% da receita das lojas, segundo a GMattos. Em calçados, o desafio é a inadimplência, que chegou a 9,53%, a maior do varejo segundo a CNDL/SPC Brasil. Ele compensa quando a cobrança é constante: lembrete antes do vencimento, mensagem no dia e negociação no atraso.",
        },
        {
          question: "Qual é a inadimplência média em lojas de calçados?",
          answer:
            "Segundo a CNDL/SPC Brasil, a inadimplência em calçados chegou a 9,53%, o maior índice do varejo nacional. Nas lojas que começam a usar a Fidexa, ela costuma estar entre 15% e 30% ao ano, podendo chegar a 50% em alguns meses.",
        },
        {
          question: "Como cobrar o cliente do crediário sem constranger?",
          answer:
            "Muitas vezes o atraso é só esquecimento, por isso o primeiro passo é o lembrete antes do vencimento. Depois, as mensagens seguem o modelo de template aprovado pela Meta, e o próprio cliente escolhe a parcela e paga pelo Fidexa Pay, sem ligação nem cobrança no balcão.",
        },
        {
          question:
            "A Fidexa funciona com o sistema da minha loja de calçados?",
          answer:
            "A Fidexa se integra por API ao ERP TOTVS Linx. Com outros sistemas, a carteira entra por planilha com clientes, valores devidos e datas de vencimento, e a importação é feita pela Fidexa sem custo adicional. Você não precisa trocar o seu sistema de gestão.",
        },
        {
          question: "Dá para cobrar parcelas pequenas?",
          answer:
            "Sim. A Fidexa cobra dívidas de qualquer valor, inclusive as de R$ 50 ou menos, que muitas lojas dão como perdidas porque o custo de levar à Justiça é maior do que a própria dívida.",
        },
        {
          question: "A Fidexa recupera dívidas que já foram negativadas?",
          answer:
            "Sim. Além das parcelas em dia e em atraso, a Fidexa também é usada para recuperar dívidas já encaminhadas aos órgãos de proteção ao crédito.",
        },
        {
          question: "Funciona para uma rede com várias lojas de calçados?",
          answer:
            "Sim. A carteira de todas as lojas fica num painel único, e o atendimento no WhatsApp continua separado por loja, num chat só para a rede inteira.",
        },
        {
          question: "Como o cliente paga a parcela?",
          answer:
            "Pelo Fidexa Pay: o cliente recebe a cobrança no WhatsApp, escolhe a parcela e paga por Pix na própria conversa. A Fidexa dá a baixa e registra o pagamento, sem ninguém da loja conferir extrato.",
        },
        {
          question: "O WhatsApp da loja corre risco de bloqueio?",
          answer:
            "A Fidexa usa a API oficial do WhatsApp e mensagens no modelo de template aprovado pela Meta, que é o caminho previsto pela própria Meta para mensagens de cobrança, em vez de disparos pelo WhatsApp comum da loja.",
        },
        {
          question: "Quanto custa a Fidexa?",
          answer:
            "O valor depende do tamanho da operação. A demonstração é gratuita: um especialista mostra a Fidexa com a carteira da sua loja e apresenta a proposta.",
        },
      ],
    },
    {
      type: "finalCta",
      id: "cta-final",
      title: "Veja a régua de cobrança da sua loja de calçados funcionando",
      text: "Demonstração gratuita com um especialista, usando a carteira da sua loja.",
      ctaLabel: "Agendar demonstração gratuita",
    },
  ],
};
